/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import "gsap/EasePack";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText);

import Swiper from "swiper";
import {
	Autoplay,
	// Thumbs,
	// EffectFade,
	Navigation,
	Pagination,
	Scrollbar,
} from "swiper/modules";

import { Fancybox } from "@fancyapps/ui";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// Default : Smooth scrolling
let scrollSmooth = false;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'no-js' body class
	document.body.classList.remove("no-js");
	gsapPreloader();
	scrollDetection(50);
	menuBurger();
	menuDropdown();
	initBanner();
	bannerToMarquee();
	popinInit();
	toggleInit();
	accordionInit();
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
});

// DOCUMENT MOUSEMOVE
document.addEventListener("mousemove", (event) => {
	cursorAnimation();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(bool) {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		}

		document.documentElement.style.overflowY = enableScroll ? "auto" : "hidden";
	}
}

// Scroll vers une ancre
function scrollToAnchor(scrollOffset = 55) {
	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll('a[href^="#"]');
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll("[data-scrollto]");
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
}

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 0) => {
	let position = window.scrollY;

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		let scroll = window.scrollY;
		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}
		// Scroll vers le haut
		else {
			document.body.classList.remove("page-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}
		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurgerButton = document.querySelector("#page-header-burger");
	const headerBurgerArea = document.querySelector("#page-header");

	if (headerBurgerButton) {
		headerBurgerButton.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullscreenmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullscreenmenu");
			}
		});

		document.addEventListener("mouseup", (event) => {
			if (
				document.body.classList.contains("page-with-fullscreenmenu") &&
				!headerBurgerButton.contains(event.target) &&
				headerBurgerButton !== event.target &&
				!headerBurgerArea.contains(event.target) &&
				headerBurgerArea !== event.target
			) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			}
		});
	}
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const initBanner = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
};

// Gestions des actions des [data-toggle]
const toggleInit = () => {
	// Récupération de tous les écouteurs
	const toggleListeners = document.querySelectorAll("[data-toggle]");

	// Écouteur d'événement pour chaque écouteur
	toggleListeners.forEach((togglelistener) => {
		const toggleTargetSelector = togglelistener.dataset.toggle;
		const toggleTarget = toggleTargetSelector
			? document.querySelector(toggleTargetSelector)
			: null;

		togglelistener.addEventListener("click", (event) => {
			if (toggleTarget) {
				// event.stopPropagation();
				toggleTarget.classList.toggle("--active");
			}
		});
	});

	// Gestionnaire pour les clics hors des éléments toggle
	document.addEventListener("click", (event) => {
		toggleListeners.forEach((togglelistener) => {
			const toggleTargetSelector = togglelistener.dataset.toggle;
			const toggleTarget = toggleTargetSelector
				? document.querySelector(toggleTargetSelector)
				: null;
			if (
				toggleTarget &&
				toggleTarget.classList.contains("--active") &&
				!toggleTarget.contains(event.target) &&
				!togglelistener.contains(event.target)
			) {
				toggleTarget.classList.remove("--active");
			}
		});
	});
};

// Gestions des actions des [data-accordion]
const accordionInit = () => {
	const accordionListeners = document.querySelectorAll("[data-accordion]");

	accordionListeners.forEach((accordionElement) => {
		accordionElement.addEventListener("click", (event) => {
			event.stopPropagation();

			// Retirer la classe "--active" à tous les accordionListeners et accordionTargets
			accordionListeners.forEach((element) => {
				if (element !== accordionElement) {
					const accordionTarget = document.getElementById(
						element.dataset.accordion
					);
					if (accordionTarget) {
						element.classList.remove("--active");
						accordionTarget.classList.remove("--active");
					}
				}
			});

			// Ajouter la classe "--active" à la cible
			if (accordionElement.dataset.accordion) {
				const currentaccordionTarget = document.getElementById(
					accordionElement.dataset.accordion
				);
				if (currentaccordionTarget) {
					accordionElement.classList.add("--active");
					currentaccordionTarget.classList.add("--active");
				}
			}
		});
	});
};

// Gestions des actions des [data-popin]
const popinInit = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(
					`.page__popin#${thispopinName}`
				);
				thispopinEl.classList.toggle("page__popin--open");
			}

			// mettre les vidéos des popins en pause
			const htmlVideoFrompopin =
				document.querySelectorAll(".page__popin video");
			if (htmlVideoFrompopin.length > 0) {
				htmlVideoFrompopin.forEach((video) => {
					video.pause();
				});
			}
		});
	});

	document.addEventListener("mouseup", (event) => {
		// si une popin est ouverte
		if (
			document.querySelectorAll(".page__popin--open").length > 0
		) {
			// clic en dehors de la zone
			if (
				!document
					.querySelector(".page__popin--open .popin__wrapper")
					.contains(event.target) &&
				document.querySelector(".page__popin--open .popin__wrapper") !== event.target
			) {
				// fermer la popin
				document.querySelectorAll(".page__popin--open").forEach((popin) => {
					popin.classList.remove("page__popin--open");
				});

				// mettre les vidéos des popins en pause
				const htmlVideoFrompopin =
					document.querySelectorAll(".page__popin video");
				if (htmlVideoFrompopin.length > 0) {
					htmlVideoFrompopin.forEach((popinVideo) => {
						popinVideo.pause();
					});
				}
			}
		}
	});
};

// Convertir les [data-src] en [src]
const srcInit = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
function swiperInit() {
	if (
		// .section-archive-related-event
		document.querySelectorAll("#autres-actualites .swiper .swiper-slide")
			.length > 1
	) {
		new Swiper("#autres-actualites .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			// Modules
			modules: [Scrollbar],
			scrollbar: {
				el: "#autres-actualites .swiper-scrollbar",
				draggable: true,
			},
		});
	}

	if (
		// .section-archive-related-projets
		document.querySelectorAll("#autres-projets .swiper .swiper-slide").length >
		1
	) {
		new Swiper("#autres-projets .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			// Modules
			modules: [Scrollbar],
			scrollbar: {
				el: "#autres-projets .swiper-scrollbar",
				draggable: true,
			},
		});
	}

	if (
		// .section-archive-related-evenements
		document.querySelectorAll("#autres-evenements .swiper .swiper-slide").length >
		1
	) {
		new Swiper("#autres-evenements .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			// Modules
			modules: [Scrollbar],
			scrollbar: {
				el: "#autres-evenements .swiper-scrollbar",
				draggable: true,
			},
		});
	}

	if (
		// .section-archive-featured-news
		document.querySelectorAll("#dernieres-actualites .swiper .swiper-slide")
			.length > 1
	) {
		new Swiper("#dernieres-actualites .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			centeredSlides: true,
			// Modules
			modules: [Autoplay, Pagination],
			autoplay: {
				delay: 3200,
				disableOnInteraction: true,
				pauseOnMouseEnter: true,
			},
			pagination: {
				el: "#dernieres-actualites .swiper-pagination",
				clickable: true,
			},
			// Events
			on: {
				init: function () {
					// Ajouter une classe à la slide initialement au centre
					this.slides[this.activeIndex].classList.add("--centered");
				},
				slideChange: function () {
					// Supprimer la classe de toutes les slides
					this.slides.forEach((slide) => {
						slide.classList.remove("--centered");
					});
					// Ajouter une classe à la nouvelle slide au centre
					this.slides[this.activeIndex].classList.add("--centered");
				},
			},
		});
	}

	if (
		// .section-archive-featured-event
		document.querySelectorAll("#prochains-evenements .swiper .swiper-slide")
			.length > 1
	) {
		new Swiper("#prochains-evenements .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			// Modules
			modules: [Navigation, Scrollbar],
			navigation: {
				nextEl: "#prochains-evenements .swiper-nav-next",
				prevEl: "#prochains-evenements .swiper-nav-prev",
				disabledClass: "--disabled",
				hiddenClass: "--hidden",
			},
			scrollbar: {
				el: "#prochains-evenements .swiper-scrollbar",
				draggable: true,
			},
		});
	}
}

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
function fancyboxInit() {
	Fancybox.bind("[data-fancybox]", {
		hideScrollbar: true,
	});
}

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
function initScrollSmooth() {
	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
}

// GSAP : Découpage des textes (avec SplitText)
function gsapSplitText() {
	const textsToAnimated = document.querySelectorAll([
		// global
		'[data-animation^="reveal-text"]',
		'[data-animation^="reveal-title"]',
		".elementor-heading-title",
	]);

	textsToAnimated.forEach((_thisText) => {
		new SplitText(_thisText, {
			tag: "span",
			type: "lines, words",
			// type: "lines, words, chars",
			linesClass: "line",
			wordsClass: "word",
			charsClass: "char",
		});
	});
}

// GSAP : Animations au scroll (avec ScrollTrigger)
function gsapAnimations() {
	// [data-animation] + .elementor-element
	const dataAnimation = document.querySelectorAll([
		"[data-animation]",
		".elementor-heading-title",
		".elementor-element > .elementor-element > .elementor-element:not([data-animation])",
	]);
	dataAnimation.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// data-animation="counter"
	const dataCounter = document.querySelectorAll('[data-animation="counter"]');
	dataCounter.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0%",
			// markers: true,
			onEnter: () => {
				setTimeout(() => {
					gsap.from(_this, {
						duration: 1.5,
						textContent: 0,
						snap: { textContent: 1 },
						stagger: {
							each: 1.0,
							onUpdate: function () {
								this.targets()[0].innerHTML = Math.ceil(
									this.targets()[0].textContent
								);
								// conserver les "."
								// .toString()
								// .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
								// conserver les espaces
								// .toString()
								// .replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
							},
						},
					});
				}, delay);
			},
		});
	});

	// data-animation="reveal-text-reading"
	const dataRevealTextReading = document.querySelectorAll([
		'[data-animation="reveal-text-reading"]',
	]);
	dataRevealTextReading.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		gsap.fromTo(
			_this.querySelectorAll(".word"),
			{
				opacity: 0.33,
			},
			{
				delay: delay,
				stagger: 0.3,
				ease: "power1.out",
				opacity: 1,
				scrollTrigger: {
					trigger: _this,
					start: "top 88%",
					end: "top 22%",
					scrub: true,
					// markers: true,
				},
			}
		);
	});

	// data-animation="sticky"
	const dataAnimationSticky = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-animation="sticky"]',
	]);
	dataAnimationSticky.forEach((_this) => {
		gsap.to(_this, {
			scrollTrigger: {
				trigger: _this,
				pin: true,
				pinSpacing: false,
				start: "top top",
				end: () => {
					// Calcule la position de fin basée sur les hauteurs de l'élément et de son parent
					const endPos =
						"+=" + (_this.parentElement.offsetHeight - _this.offsetHeight);
					return endPos;
				},
				// markers: true,
			},
		});
	});

	// data-marquee="left|right"
	const dataMarquee = document.querySelectorAll("[data-marquee]");
	dataMarquee.forEach((marquee) => {
		// find number of content clone needed
		let content = marquee.firstElementChild;
		if (content) {
			let contentWidth = content.offsetWidth;
			let widthQuotient = Math.ceil(window.innerWidth / contentWidth) + 2;
			for (let i = 0; i < widthQuotient; i++) {
				let clone = content.cloneNode(true);
				content.after(clone);
			}
		}
		gsap.fromTo(
			marquee,
			{
				x: marquee.dataset.marquee == "right" ? "-5vw" : "5vw",
			},
			{
				x: marquee.dataset.marquee == "right" ? "5vw" : "-5vw",
				scrollTrigger: {
					scrub: 2,
					trigger: marquee,
					start: "50% 100%",
					end: "50% 0%",
					// markers: true,
				},
			}
		);
	});
}

// Actions exécutées après le preLoader d'accueil
function afterPreloader() {
	scrollEnabled(true);
	swiperInit();
	fancyboxInit();
	gsapAnimations();
	scrollToAnchor();
}

// GSAP : Animation sur le #cursor
const cursorAnimation = () => {
	const cursor = document.querySelector("#cursor");
	if (cursor) {
		gsap.to(cursor, 3.6, {
			x: event.clientX,
			y: event.clientY,
			ease: "back.out(2)",
			delay: 0.1,
		});
	}
};

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	scrollEnabled(false);
	gsapSplitText();

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();

	if (document.body.classList.contains("home")) {
		loaderTimeline
			// animation
			.to("#page-preloader .preloader__logo", {
				duration: 0.6,
				ease: "power3.out",
				opacity: 1,
				y: 0,
			})
			.to("#page-preloader .preloader__logo svg:first-child", {
				duration: 0.6,
				ease: "power2.out",
				clipPath: "inset(0 0 100% 0)",
			})
			.to("#page-preloader .preloader__wrapper", {
				duration: 0.15,
				ease: "power1.out",
				opacity: 0,
			})
			.set("#page-preloader", {
				visibility: "hidden",
			});
	} else {
		loaderTimeline
			// animation
			.to("#page-preloader .preloader__wrapper", {
				delay: 0.3,
				duration: 0.3,
				ease: "power1.out",
				opacity: 0,
			})
			.set("#page-preloader", {
				visibility: "hidden",
			});
	}

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.3);
};
